import { useCallback, useEffect, useState, type RefObject } from "react";

/**
 * Pantalla completa de un contenedor con la API nativa del navegador
 * (`element.requestFullscreen()` / `document.exitFullscreen()`).
 *
 * Tres decisiones, cada una con test:
 *
 * - **El estado lo fija el evento `fullscreenchange`, NUNCA el clic.** Escape
 *   sale de pantalla completa sin pasar por el boton; si el estado cambiara al
 *   pulsar, tras Escape el boton diria "Salir" con la pantalla ya normal y el
 *   siguiente clic llamaria a `exitFullscreen` en vez de entrar. Tambien es lo
 *   que hace que un `requestFullscreen` rechazado (permiso denegado) no deje el
 *   boton mintiendo: sin evento, no hay cambio.
 * - **`activa` es "es MI contenedor el que esta a pantalla completa"**, no "hay
 *   algo a pantalla completa": el evento se dispara en el documento para
 *   cualquier elemento (un video de otra parte de la pagina).
 * - **`disponible` sale de `document.fullscreenEnabled`.** Es falso donde la
 *   API no existe (jsdom) y dentro de un iframe sin `allowfullscreen`, que es
 *   donde vive el widget. Ahi el boton no se pinta: uno que no hace nada es
 *   peor que ninguno.
 *
 * Modulo aparte del componente por `react-refresh/only-export-components`,
 * mismo criterio que `vistaAsignaciones.ts`.
 */
export function usePantallaCompleta(ref: RefObject<HTMLElement | null>): {
  disponible: boolean;
  activa: boolean;
  alternar: () => void;
} {
  // `=== true` y no truthy: en jsdom la propiedad no existe (undefined) y en
  // un iframe sin permiso es `false`; los dos casos son "no hay boton".
  const disponible = typeof document !== "undefined" && document.fullscreenEnabled === true;
  const [activa, setActiva] = useState(false);

  useEffect(() => {
    if (!disponible) return;
    const alCambiar = () => {
      setActiva(ref.current !== null && document.fullscreenElement === ref.current);
    };
    document.addEventListener("fullscreenchange", alCambiar);
    return () => document.removeEventListener("fullscreenchange", alCambiar);
  }, [disponible, ref]);

  const alternar = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const peticion =
      document.fullscreenElement === el ? document.exitFullscreen() : el.requestFullscreen();
    // Un rechazo (permiso denegado, no iniciado por gesto del usuario) no tiene
    // nada que hacer aqui: el estado viene del evento, que en ese caso no llega,
    // y el boton se queda como estaba, que es la verdad.
    void peticion.catch(() => {});
  }, [ref]);

  return { disponible, activa, alternar };
}
